import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { HackerDashboardsShow } from '@/types';
import { EventLocationCard } from './components/EventLocationCard';
import { HackathonCountdown } from './components/HackathonCountdown';
import { JudgingResultsCard } from './components/JudgingResultsCard';
import { ProjectSubmissionCard } from './components/ProjectSubmissionCard';

// /:event_slug/hacker/dashboard — the port of the 26-ar and 26-mx twins as one page
// (PORTING.md G5). What differed is the edition's display face (`titleFont`), whether the
// GitHub avatar sits next to the info block, the countdown digit style and the venue.

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function extractGithubUsername(github: string | null): string {
  if (!github) return '';

  if (github.includes('github.com')) {
    const parts = github.split('/');
    return parts[parts.length - 1] || github;
  }

  return github;
}

function githubHref(github: string): string {
  return github.startsWith('http') ? github : `https://github.com/${github}`;
}

export default function Show({
  dashboard,
  event,
  venue,
  titleFont,
  countdownDigitClass,
  showAvatar,
  submissionHint,
  judgingResults,
}: HackerDashboardsShow) {
  const githubUsername = extractGithubUsername(dashboard.hacker.github);
  const now = new Date();
  const showCountdown = Boolean(
    (event.startsAt && now < new Date(event.startsAt)) ||
      (event.hackingEndsAt && now < new Date(event.hackingEndsAt)),
  );

  const info = (
    <>
      <div className="border-primary/20 border-b pb-4">
        <h2
          className={cn(
            'font-bold text-2xl text-primary sm:text-3xl md:text-4xl',
            titleFont,
          )}
        >
          TU INFORMACIÓN
        </h2>
      </div>

      <div className="space-y-4">
        <div className="border-primary border-l-4 pl-4">
          <h3
            className={cn(
              'font-bold text-primary/70 text-sm uppercase',
              titleFont,
            )}
          >
            Nombre Completo
          </h3>
          <p
            className={cn(
              'font-bold text-lg text-primary sm:text-xl',
              titleFont,
            )}
          >
            {dashboard.hacker.fullName}
          </p>
        </div>

        {dashboard.hacker.github && (
          <div className="border-primary border-l-4 pl-4">
            <h3
              className={cn(
                'font-bold text-primary/70 text-sm uppercase',
                titleFont,
              )}
            >
              GitHub
            </h3>
            <a
              href={githubHref(dashboard.hacker.github)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-primary text-sm transition-colors hover:underline sm:text-base"
            >
              {githubUsername}
            </a>
          </div>
        )}

        {dashboard.hackerProfile?.discordUsername && (
          <div className="border-primary border-l-4 pl-4">
            <h3
              className={cn(
                'font-bold text-primary/70 text-sm uppercase',
                titleFont,
              )}
            >
              Discord
            </h3>
            <p className="font-mono text-primary text-sm sm:text-base">
              {dashboard.hackerProfile.discordUsername}
            </p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl space-y-8">
          <div className="text-center">
            <h1
              className={cn(
                'inline-block font-bold text-2xl text-primary sm:text-3xl md:text-4xl',
                titleFont,
              )}
            >
              Bienvenid@ a{' '}
              <span className="bg-primary px-2 py-1 text-background">
                {event.name}
              </span>
              , {githubUsername || dashboard.hacker.fullName.split(' ')[0]}
            </h1>
          </div>

          {showCountdown && (
            <HackathonCountdown
              startsAt={event.startsAt}
              hackingEndsAt={event.hackingEndsAt}
              eventName={event.name}
              titleFont={titleFont}
              digitClass={countdownDigitClass}
            />
          )}

          <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
            {showAvatar ? (
              <div className="flex flex-col-reverse items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
                <div className="w-full flex-1 space-y-6">{info}</div>

                {githubUsername && (
                  <Avatar className="h-28 w-28 shrink-0 border-2 border-primary sm:h-32 sm:w-32">
                    <AvatarImage
                      src={`https://github.com/${githubUsername}.png`}
                      alt={`${githubUsername} avatar`}
                    />
                    <AvatarFallback className="text-2xl">
                      {getInitials(dashboard.hacker.fullName)}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            ) : (
              <div className="space-y-6">{info}</div>
            )}
          </div>

          {dashboard.team && (
            <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
              <div className="text-center">
                <h2
                  className={cn(
                    'mb-2 font-bold text-primary/70 text-sm uppercase tracking-wide',
                    titleFont,
                  )}
                >
                  Tu Equipo
                </h2>
                <div className="inline-block bg-primary px-6 py-3">
                  <p
                    className={cn(
                      'font-bold text-3xl text-background sm:text-4xl md:text-5xl',
                      titleFont,
                    )}
                  >
                    {dashboard.team.slug}
                  </p>
                </div>
                {dashboard.team.tableNumber && (
                  <p className={cn('mt-4 text-primary/70 text-sm', titleFont)}>
                    Mesa:{' '}
                    <span className="bg-primary px-2 py-1 font-bold text-background">
                      {dashboard.team.tableNumber}
                    </span>
                  </p>
                )}
                {dashboard.team.track && (
                  <p className={cn('mt-4 text-primary/70 text-sm', titleFont)}>
                    Track:{' '}
                    <span className="bg-primary px-2 py-1 font-bold text-background">
                      {dashboard.team.track.name}
                    </span>
                  </p>
                )}
                {dashboard.team.mentor && (
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <Avatar className="h-10 w-10 border-2 border-primary">
                      <AvatarImage
                        src={dashboard.team.mentor.pictureUrl ?? undefined}
                        alt={dashboard.team.mentor.fullName}
                      />
                      <AvatarFallback>
                        {getInitials(dashboard.team.mentor.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <p className={cn('text-primary/70 text-sm', titleFont)}>
                      Mentor:{' '}
                      <span className="bg-primary px-2 py-1 font-bold text-background">
                        {dashboard.team.mentor.fullName}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-8 space-y-4">
                <h3
                  className={cn(
                    'font-bold text-primary/70 text-sm uppercase tracking-wide',
                    titleFont,
                  )}
                >
                  Miembros
                </h3>
                <div className="space-y-3">
                  {dashboard.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex flex-col gap-1 border-primary border-l-4 pl-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <h4
                          className={cn(
                            'font-bold text-lg text-primary sm:text-xl',
                            titleFont,
                          )}
                        >
                          {member.fullName}
                          {member.id === dashboard.hacker.id && (
                            <span
                              className={cn(
                                'ml-2 text-primary/70 text-sm',
                                titleFont,
                              )}
                            >
                              (tú)
                            </span>
                          )}
                        </h4>
                        {member.github && (
                          <a
                            href={githubHref(member.github)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-primary/70 text-sm transition-colors hover:text-primary hover:underline"
                          >
                            {extractGithubUsername(member.github)}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {judgingResults.map((result) => (
            <JudgingResultsCard
              key={result.stageId}
              result={result}
              titleFont={titleFont}
            />
          ))}

          {dashboard.team?.project && (
            <ProjectSubmissionCard
              project={dashboard.team.project}
              titleFont={titleFont}
              submissionHint={submissionHint}
            />
          )}

          <EventLocationCard
            eventName={event.name}
            venue={venue}
            titleFont={titleFont}
          />
        </div>
      </div>
    </div>
  );
}
